import * as React from 'react'
import { Updater } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { dataLayerServices } from '../../../../seo/datalayer'
import type { State } from '../../../cmsPage.widget'
import { fixTableStructure, htmlDecode } from '../../../utils/html'
import type { ActionProps, DownloadBlockNested } from '../../modernBlocks.state'
import { VideoLayout } from '../mediaBlock/mediaVideo.layout'
import { LinkListBlockNestedLayout } from '../nestedBlocks/linkListBlockNested.layout'
import { MediaBlockNestedLayout } from '../nestedBlocks/mediaBlockNested.layout'
import { QuoteBlockNestedLayout } from '../nestedBlocks/quoteBlockNested.layout'
import { TextBlockNestedLayout } from '../nestedBlocks/textBlockNested.layout'
import { SharedBlockLayout } from '../shared/sharedBlock.layout'
import type { ExpandableBlockClassNames, ExpandableItem } from './expandableBlock.state'
import { DownloadBlockNestedLayout } from '../nestedBlocks/downloadBlockNested.layout'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'

export const expandableBlockClasses: ExpandableBlockClassNames = {
  // DEFAULTS
  block: 'block block--list block--expand',
  inview: 'inview',
  inner: 'block__inner ls-container ls-gutter ls-spacing--s',
  header: 'block__header',
  label: 'block__label text--s',
  title: 'block__title title--m',
  intro: 'block__intro text--l',
  content: 'block__content',
  footer: 'block__footer ls-clickables',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary',
  clickableSecondary: 'block__button button button--tertiary',

  // BLOCK SPECIFIC
  list: 'expand',
  item: 'expand__item',
  itemInner: 'expand__inner',
  itemSummary: 'expand__summary',
  itemTitle: 'expand__title',
  itemDescription: 'expand__description text--s',
  itemText: 'expand__content text--styled',
  itemChildren: 'expand__children',
  itemNestedBlocks: 'expand__blocks'
}

export const ExpandableLayout = {
  Inner: (props: { children?: React.ReactNode }): JSX.Element => {
    const { children } = props
    return (
      <div className={getClassNameModern(expandableBlockClasses, 'inner', 'mw-container-s mx-auto px-gutter')}>
        {children}
      </div>
    )
  },
  Content: (
    props: {
      label?: string
      title?: string
      intro?: string
      items?: ExpandableItem[]
      rootState: State
      setRootState: (_: Updater<State>) => void
      anchor?: string
    } & ActionProps
  ) => {
    const {
      label,
      title,
      intro,
      items,
      showPrimary,
      showSecondary,
      primaryAction,
      secondaryAction,
      rootState,
      setRootState,
      anchor
    } = props
    return (
      <>
        {(label || title || intro) && (
          <header className={getClassNameModern(expandableBlockClasses, 'header', '')}>
            {label && (
              <p className={getClassNameModern(expandableBlockClasses, 'label', 'fs-6 text-primary text-uppercase')}>
                {label}
              </p>
            )}
            {title && <h2 id={slugify(htmlDecode(title))} className={getClassNameModern(expandableBlockClasses, 'title', 'h2 mb-3')}>{title}</h2>}
            {intro && (
              <p
                className={getClassNameModern(expandableBlockClasses, 'intro', 'text--styled mb-3')}
                dangerouslySetInnerHTML={{ __html: intro }}
              />
            )}
          </header>
        )}

        <div className={getClassNameModern(expandableBlockClasses, 'content', '')}>
          <ExpandableLayout.Items
            items={items}
            rootState={rootState}
            setRootState={setRootState}
          />

          {(showPrimary || showSecondary) && (
            <footer className={getClassNameModern(expandableBlockClasses, 'footer', 'pt-5')}>
              {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
                <div
                className={getClassNameModern(expandableBlockClasses, 'clickableWrapper', 'd-block d-sm-inline-block')}
                >
                  <SharedBlockLayout.Clickable
                    id="expandPrimaryAction"
                    type="button"
                    link={primaryAction}
                    classNames={getClassNameModern(expandableBlockClasses, 'clickablePrimary', 'btn btn-primary')}
                  />
                </div>
              )}
              {showSecondary && secondaryAction && secondaryAction.title && secondaryAction.url && (
                <div
                  className={getClassNameModern(
                    expandableBlockClasses,
                    'clickableWrapper',
                    'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                  )}
                >
                  <SharedBlockLayout.Clickable
                    id="expandSecondaryAction"
                    type="button"
                    link={secondaryAction}
                    classNames={getClassNameModern(
                      expandableBlockClasses,
                      'clickableSecondary',
                      'btn btn-outline-primary'
                    )}
                  />
                </div>
              )}
            </footer>
          )}
        </div>
      </>
    )
  },
  Items: (props: {
    items: ExpandableItem[] | undefined
    rootState: State
    setRootState: (_: Updater<State>) => void
  }): JSX.Element => {
    const { items } = props
    const nestedDownloadBlock = items ? findNestedDownloadBlock(items) : undefined
    return <>
      {items && items.length > 0 && (
        <ul className={getClassNameModern(expandableBlockClasses, 'list', 'list-group mt-5')}>
          {items.map((item, key) => (
            <ExpandableLayout.Item
              key={`expandable-item-${key}`}
              id={item.anchor ? item.anchor : `expandable-item-${key}`}
              item={item}
              nestedDownloadBlock={nestedDownloadBlock}
            >
              {item.nestedBlocks.map((nb, n) =>
                nb.kind == 'textNested' ? (
                  <TextBlockNestedLayout key={nb.index} {...nb} />
                ) : nb.kind == 'quoteNested' ? (
                  <QuoteBlockNestedLayout key={nb.index} {...nb} />
                ) : nb.kind == 'mediaNested' ? (
                  <MediaBlockNestedLayout
                    {...nb}
                    key={`${key}-${nb.index}`}
                    videoChild={
                      <VideoLayout
                        mediaBlock={{ ...nb, type: 'video', url: (nb.type == 'video' && nb.url) || '' }}
                        rootState={props.rootState}
                        setState={props.setRootState}
                      />
                    }
                  />
                ) : nb.kind == 'linkListNested' ? (
                  <LinkListBlockNestedLayout key={nb.index} {...nb} />
                ) : (
                  <></>
                )
              )}
            </ExpandableLayout.Item>
          ))}
        </ul>
      )}
    </>
  },
  Item: (props: {
    id: string
    item: ExpandableItem
    nestedDownloadBlock?: DownloadBlockNested
    children: React.ReactNode | null
  }): JSX.Element => {
    const { id, item, children } = props
    return (
      <li className={getClassNameModern(expandableBlockClasses, 'item', 'list-group-item')} key={`expand-${id}`}>
        <details className={getClassNameModern(expandableBlockClasses, 'itemInner', '')} open={item.isOpen} onClick={() => dataLayerServices.expandableDataLayer(item.title)}>
          <summary
            id={id}
            className={getClassNameModern(expandableBlockClasses, 'itemSummary', 'mb-0 py-2 text-secondary')}
          >
            <p className={getClassNameModern(expandableBlockClasses, 'itemTitle', 'h3')}>{item.title}</p>
            {item.description && (
              <p className={getClassNameModern(expandableBlockClasses, 'itemDescription', 'small')}>
                {item.description}
              </p>
            )}
          </summary>
          <div className={getClassNameModern(expandableBlockClasses, 'itemChildren', '')}>
            {props.nestedDownloadBlock && <DownloadBlockNestedLayout {...props.nestedDownloadBlock} />}
            {item.body ? (
              <div
                className={getClassNameModern(expandableBlockClasses, 'itemText', 'text--styled')}
                dangerouslySetInnerHTML={{ __html: fixTableStructure(item.body) }}
              />
            ) : (
              <></>
            )}

            {children != null ? (
              <div className={getClassNameModern(expandableBlockClasses, 'itemNestedBlocks', '')}>{children}</div>
            ) : (
              <></>
            )}
          </div>
        </details>
      </li>
    )
  },
}

const findNestedDownloadBlock = (items: ExpandableItem[]) => {
  for (let item of items) {
    const downloadBlock = item.nestedBlocks.find((block) => block.kind === 'downloadNested')
    if (downloadBlock) {
      return downloadBlock as DownloadBlockNested
    }
  }
  return undefined
}
